import type { HTMLAttributes, ReactNode } from 'react';
import { faInfoCircle } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Heading2, MutedText } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

export interface SectionHeaderProps extends HTMLAttributes<'main'> {
  title: string;
  titleClassName?: string;
  description?: ReactNode;
  descriptionClassName?: string;
  helperText?: ReactNode;
  actions?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const sizes: Record<
  NonNullable<SectionHeaderProps['size']>,
  { title: string; description: string }
> = {
  sm: { title: 'text-xl', description: 'text-xs' },
  md: { title: 'text-2xl', description: 'text-sm' },
  lg: { title: 'text-3xl', description: 'text-sm' },
};

function SectionHeader({
  title,
  titleClassName,
  description,
  descriptionClassName,
  helperText,
  actions,
  size = 'md',
  className,
  children,
}: SectionHeaderProps) {
  const { title: titleClass, description: descriptionClass } = sizes[size];

  return (
    <header
      className={cn(
        'flex w-full max-w-full flex-col items-start gap-2 md:items-center md:justify-between',
        size === 'sm' ? 'flex-row' : 'flex-col md:flex-row',
        className,
      )}
    >
      <div
        className={cn(
          'flex min-w-0 grow flex-col overflow-hidden',
          size === 'sm' ? 'gap-0' : 'gap-1',
        )}
      >
        <Heading2 className={cn('overflow-hidden font-bold', titleClass, titleClassName)}>
          {title}
        </Heading2>
        {description && (
          <MutedText className={cn('overflow-hidden', descriptionClass, descriptionClassName)}>
            {description}
            {helperText && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      colorScheme="secondary"
                      size="icon-sm"
                      aria-label="Additional information"
                    >
                      <Icon icon={faInfoCircle} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{helperText}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </MutedText>
        )}
      </div>
      {actions && <div className="flex flex-col gap-2">{actions}</div>}
      {children}
    </header>
  );
}

export default SectionHeader;
